import { memo, useMemo } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ScheduleIcon from '@mui/icons-material/Schedule'
import { Button } from 'Common/Supplyvue-ui'
import { useSchedulerData } from 'Features/SchedulerView/SchedulerView.data'
import { InputRow } from 'Features/SchedulerView/SchedulerView.types'
import { WeekResult } from './Scheduler.data'
import { makespanForWeek } from './makespan'

const TOTAL_HOURS = 168

// ─── Stats computation (shared with ScheduleViewPage) ───────────────

/**
 * `weekBaseHour` comes from `weekBaseHourFor`: `null` for a single-week
 * schedule, `0` for week 1 of a consolidated run, `168` for week 2. Only the
 * makespan reads it — everything else below is a duration or a count and so
 * is offset-free. Without it a consolidated run's week 2 reports where its
 * last run ends in the whole horizon rather than in the week. See makespan.ts.
 */
export function computeScheduleStats(
  rows: InputRow[],
  weekBaseHour: number | null = null
) {
  if (!rows.length) return null

  const makespan = makespanForWeek(rows, weekBaseHour)

  const totalLineHours = rows.reduce(
    (sum, row) => sum + ((row.line_end_time ?? 0) - (row.line_start_time ?? 0)),
    0
  )

  const uniqueProducts = new Set(rows.map((row) => row.productCode))
  const uniqueLines = new Set(rows.map((row) => row.line))
  const uniqueKitchens = new Set(rows.map((row) => row.kitchen))
  const totalQuantity = rows.reduce((sum, row) => sum + (row.quantity ?? 0), 0)

  // Changeover count
  const kitchenRows: Record<string, InputRow[]> = {}
  rows.forEach((row) => {
    if (!kitchenRows[row.kitchen]) kitchenRows[row.kitchen] = []
    kitchenRows[row.kitchen].push(row)
  })
  let changeovers = 0
  Object.values(kitchenRows).forEach((kRows) => {
    kRows.sort(
      (a, b) => (a.kitchen_start_time ?? 0) - (b.kitchen_start_time ?? 0)
    )
    for (let i = 1; i < kRows.length; i++) {
      if (kRows[i].base_material !== kRows[i - 1].base_material) changeovers++
    }
  })

  // Line utilization
  const lineHours = new Map<string, number>()
  rows.forEach((row) => {
    const dur = Math.max(
      0,
      (row.line_end_time ?? 0) - (row.line_start_time ?? 0)
    )
    lineHours.set(row.line, (lineHours.get(row.line) ?? 0) + dur)
  })
  const avgLineUtil =
    uniqueLines.size > 0
      ? Math.round(
          ([...lineHours.values()].reduce((s, h) => s + h, 0) /
            (uniqueLines.size * TOTAL_HOURS)) *
            100
        )
      : 0

  return {
    makespan,
    totalLineHours,
    productCount: uniqueProducts.size,
    lineCount: uniqueLines.size,
    kitchenCount: uniqueKitchens.size,
    totalQuantity,
    changeovers,
    rowCount: rows.length,
    avgLineUtil,
  }
}

// ─── Metric Card ────────────────────────────────────────────────────

const MetricCard = memo(
  ({
    label,
    value,
    color,
  }: {
    label: string
    value: string | number
    color?: string
  }) => (
    <Box sx={{ textAlign: 'center', px: 1.5, py: 1 }}>
      <Typography
        sx={{
          fontSize: '0.6rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: 'text.disabled',
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '1.1rem',
          fontWeight: 700,
          color: color ?? 'text.primary',
          lineHeight: 1.2,
        }}
      >
        {value}
      </Typography>
    </Box>
  )
)
MetricCard.displayName = 'MetricCard'

// ─── Main Component ─────────────────────────────────────────────────

type RunSummaryProps = {
  open: boolean
  onClose: () => void
  onViewOutput: () => void
  scheduleId: string
  iteration: string
  weekResults?: Record<number, WeekResult>
}

export const SchedulerRunSummary = memo(
  ({
    open,
    onClose,
    onViewOutput,
    scheduleId,
    iteration,
    weekResults,
  }: RunSummaryProps) => {
    const { data, isLoading } = useSchedulerData(scheduleId, iteration, 1)
    const rows: InputRow[] = data?.data ?? []
    const stats = useMemo(() => computeScheduleStats(rows), [rows])

    const makespanColor = stats
      ? stats.makespan <= 120
        ? '#2e7d32'
        : stats.makespan <= 150
          ? '#ed6c02'
          : '#d32f2f'
      : undefined

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pb: 1,
            borderBottom: '2px solid',
            borderColor: 'success.main',
          }}
        >
          <CheckCircleIcon color="success" />
          Schedule Complete
        </DialogTitle>

        <DialogContent sx={{ pt: '20px !important' }}>
          {isLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={32} />
              <Typography
                sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1 }}
              >
                Loading results...
              </Typography>
            </Box>
          ) : stats ? (
            <>
              {/* Key metrics */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: 2,
                }}
              >
                <MetricCard
                  label="Makespan"
                  value={`${stats.makespan}h`}
                  color={makespanColor}
                />
                <MetricCard
                  label="Production"
                  value={`${stats.totalLineHours.toFixed(1)}h`}
                />
                <MetricCard
                  label="Quantity"
                  value={stats.totalQuantity.toLocaleString()}
                />
                <MetricCard label="Products" value={stats.productCount} />
                <MetricCard label="Changeovers" value={stats.changeovers} />
                <MetricCard
                  label="Avg Util"
                  value={`${stats.avgLineUtil}%`}
                  color={stats.avgLineUtil >= 60 ? '#2e7d32' : '#ed6c02'}
                />
              </Box>

              {/* Makespan bar */}
              <Box sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mb: 0.5,
                  }}
                >
                  <Typography
                    sx={{ fontSize: '0.65rem', color: 'text.secondary' }}
                  >
                    Makespan
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
                    {stats.makespan}h / {TOTAL_HOURS}h (
                    {Math.round((stats.makespan / TOTAL_HOURS) * 100)}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min((stats.makespan / TOTAL_HOURS) * 100, 100)}
                  sx={{
                    height: 8,
                    borderRadius: '4px',
                    bgcolor: '#f5f5f5',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: makespanColor,
                      borderRadius: '4px',
                    },
                  }}
                />
              </Box>

              {/* Per-week results (from SSE) */}
              {weekResults && Object.keys(weekResults).length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'text.secondary',
                      mb: 1,
                    }}
                  >
                    Per-Week Breakdown
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                  >
                    {Object.values(weekResults)
                      .sort((a, b) => a.week - b.week)
                      .map((wr) => (
                        <Box
                          key={wr.week}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 0.5,
                            px: 1,
                            borderRadius: 1,
                            bgcolor: 'grey.50',
                          }}
                        >
                          <ScheduleIcon
                            sx={{ fontSize: 14, color: 'text.disabled' }}
                          />
                          <Typography
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              minWidth: 52,
                            }}
                          >
                            Week {wr.week}
                          </Typography>
                          <Chip
                            label={wr.status}
                            size="small"
                            sx={{
                              fontSize: '0.6rem',
                              height: 18,
                              bgcolor:
                                wr.status === 'complete'
                                  ? 'rgba(46,125,50,0.08)'
                                  : wr.status === 'failed'
                                    ? 'rgba(211,47,47,0.08)'
                                    : 'rgba(0,0,0,0.04)',
                              color:
                                wr.status === 'complete'
                                  ? '#2e7d32'
                                  : wr.status === 'failed'
                                    ? '#d32f2f'
                                    : 'text.secondary',
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: '0.65rem',
                              color: 'text.secondary',
                              ml: 'auto',
                            }}
                          >
                            {wr.rows} rows | {wr.makespan}h
                          </Typography>
                          {wr.unmet_demand > 0 && (
                            <Typography
                              sx={{
                                fontSize: '0.65rem',
                                color: '#d32f2f',
                                fontWeight: 600,
                              }}
                            >
                              Unmet: {wr.unmet_demand}
                            </Typography>
                          )}
                        </Box>
                      ))}
                  </Box>
                </>
              )}
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                No output data available yet.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
          <Button onClick={onViewOutput} variant="contained" color="primary">
            View Full Output
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
)

SchedulerRunSummary.displayName = 'SchedulerRunSummary'
